// src/pages/LoginPage.jsx
import React, { useState } from 'react';
import { ShoppingCart, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { translations } from '../utils/translations';
import { authApi } from '../api';

const LoginPage = () => {
  const navigate = useNavigate();
  const { language, setLanguage, setCurrentUser } = useApp();
  const t = translations[language];

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  const languages = [
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'km', name: 'ខ្មែរ', flag: '🇰🇭' }
  ];

  const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username || !password) {
      setError(t.pleaseEnterCredentials || '请输入用户名和密码');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await authApi.login({ username, password });

      if (res.success) {
        // 保存 token
        localStorage.setItem('token', res.data.token);
        
        // 保存用户信息（含 store_id、store_name、store_type）
        // setCurrentUser 内部已经同步 localStorage
        setCurrentUser(res.data.user);

        console.log('✅ 登录成功:', {
          user: res.data.user.name,
          role: res.data.user.role,
          store: res.data.user.store_name,
          store_type: res.data.user.store_type
        });

        navigate('/');
      } else {
        setError(res.message || t.loginFailed || '登录失败');
      }
    } catch (err) {
      console.error('❌ 登录错误:', err);
      setError(err.message || t.loginFailed || '登录失败，请检查用户名和密码');
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    setShowLanguageMenu(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative">

        {/* 语言切换 */}
        <div className="absolute top-4 right-4">
          <div className="relative">
            <button
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <Globe className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                {currentLanguage.flag} {currentLanguage.name}
              </span>
            </button>

            {showLanguageMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors flex items-center space-x-2 ${
                      language === lang.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                    }`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span className="font-medium">{lang.name}</span>
                    {language === lang.code && (
                      <span className="ml-auto text-blue-600">✓</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 标题 */}
        <div className="text-center mb-8 mt-4">
          <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800">{t.title}</h1>
          <p className="text-gray-600 mt-2">{t.pleaseLogin}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t.username}
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => { setUsername(e.target.value); setError(''); }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="admin / cashier1"
              disabled={loading}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t.password}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="123456"
              disabled={loading}
              required
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-blue-600 text-white py-3 rounded-lg font-semibold transition-colors ${
              loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
            }`}
          >
            {loading ? (t.loggingIn || '登录中...') : t.login}
          </button>
        </form>

        <div className="mt-6 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
          <p className="font-medium mb-2">{t.testAccounts || '测试账号'}：</p>
          <p>{t.admin || '老板'}: admin / 123456</p>
          <p>{t.cashier || '收银员'}: cashier1 / 123456</p>
        </div>

        <div className="mt-4 text-center text-xs text-gray-500">
          API: http://47.79.252.120:5000
        </div>
      </div>

      {showLanguageMenu && (
        <div className="fixed inset-0 z-0" onClick={() => setShowLanguageMenu(false)} />
      )}
    </div>
  );
};

export default LoginPage;